import {
  EventReportBooking,
  TicketHolder,
} from '@components/customers/customers-table-expandable/src/customers-table-expandable.type';
import { DATE_FORMAT_SHORT_DATE_HOUR, DATE_FORMAT_SHORT_TIME } from '@constants/datetime';
import { BOOKING_DETAILS_HOLDERS_ROUTE, BOOKING_DETAILS_ROUTE } from '@constants/routes';
import { EventBookingFormatted } from '@contexts/event-report/event-report-context.type';
import { Addon } from '@domain/addon';
import { AdmissionResponse } from '@hooks/admissions/use-get-admissions';
import { VIEW_PAST } from '@hooks/use-event-navigation/use-event-navigation';
import { getDefaultAddonNameTranslation } from '@utils/get-default-addon-name-translation/get-default-addon-name-translation';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getDefaultPassNameTranslation } from '@utils/get-default-pass-name-translation/get-default-pass-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';
import { fromBackend } from '@utils/map-date/from-backend';
import { TFunction } from 'i18next';

import { currencyValueFormatter } from '../analytics-utils';

import {
  BOOKING_FILTER_ORDER,
  EVENT_REPORT_ACTION_TYPE_CANCEL,
  EVENT_REPORT_ACTION_TYPE_DOWNLOAD_PDF,
  EVENT_REPORT_ACTION_TYPE_REFUND,
  EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING,
  EVENT_REPORT_ACTION_TYPE_SEE_DETAILS,
  EVENT_REPORT_ATTENDEES_ALL,
  EVENT_REPORT_ATTENDEES_PARTIAL,
  EVENT_REPORT_BOOKING_FILTER_ACTIVE_TYPE,
  EVENT_REPORT_BOOKING_FILTER_ADDON_PARAM,
  EVENT_REPORT_BOOKING_FILTER_ATTENDANCE_PARAM,
  EVENT_REPORT_BOOKING_FILTER_ATTENDANCE_TYPE,
  EVENT_REPORT_BOOKING_FILTER_NOT_ATTENDANCE_TYPE,
  EVENT_REPORT_BOOKING_FILTER_OFFER_PARAM,
  EVENT_REPORT_BOOKING_FILTER_REFUND_PARAM,
  EVENT_REPORT_BOOKING_FILTER_REFUND_TYPE,
  EVENT_REPORT_OLD_OFFER_TYPES_TRANSLATIONS,
  EVENT_REPORT_PASS_TYPE,
} from './config';
import { BookingAction, BookingActions } from './types';

const getStatus = ({
  refundQty,
  totalQty,
  confirmed,
  price,
  translations,
  hasBeenSwapped,
}: {
  refundQty: number;
  totalQty: number;
  price: number;
  confirmed: boolean;
  translations: { [key: string]: string };
  hasBeenSwapped: boolean;
}) => {
  if (hasBeenSwapped) return translations.resoldLabel;
  if (!confirmed && price === 0) return translations.cancelledLabel;
  if (!confirmed) return translations.refundedLabel;
  if (refundQty > 0) {
    if (refundQty === totalQty) return translations.refundedLabel;

    return `${translations.refundedLabel} (${refundQty}/${totalQty})`;
  }

  return translations.activeLabel;
};

const filterBookingActions = ({
  actions,
  row,
  eventStatus,
  isSuperAdmin,
}: {
  actions: BookingActions;
  row: EventReportBooking;
  eventStatus: string;
  isSuperAdmin: boolean;
}) =>
  actions.filter((action) => {
    if (
      (!row.confirmed || row.offer.type === EVENT_REPORT_PASS_TYPE) &&
      (action.id === EVENT_REPORT_ACTION_TYPE_REFUND || action.id === EVENT_REPORT_ACTION_TYPE_CANCEL)
    )
      return false;

    if (action.id === EVENT_REPORT_ACTION_TYPE_REFUND) {
      if (!row.isRefundable && !isSuperAdmin) return false;
      if (!row.isRefundable || row.price.onlinePrice === 0) return false;
    }

    if (action.id === EVENT_REPORT_ACTION_TYPE_CANCEL) {
      if ((row.isRefundable && row.price.onlinePrice > 0) || eventStatus === VIEW_PAST) return false;
    }

    if (action.id === EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING && eventStatus === VIEW_PAST) return false;

    if (!!row.refundedAt || !!row.hasBeenSwapped) {
      if (action.id === EVENT_REPORT_ACTION_TYPE_DOWNLOAD_PDF) return false;
      if (action.id === EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING) return false;
    }

    return true;
  });

export const getBookingDetailsRoute = ({
  bookingId,
  holderCode,
  isHolder,
  clubSlug,
  eventId,
  companyLegacyId,
}: {
  bookingId: string;
  holderCode?: string | null;
  isHolder?: boolean;
  clubSlug: string;
  eventId: string;
  companyLegacyId: string;
}) => {
  const holderRoute = BOOKING_DETAILS_HOLDERS_ROUTE.replace(':companyId', companyLegacyId)
    .replace(':clubSlug', clubSlug)
    .replace(':bookingId', bookingId)
    .replace(':eventId', eventId)
    .replace(':code', holderCode ?? '');
  const bookingRoute = BOOKING_DETAILS_ROUTE.replace(':companyId', companyLegacyId)
    .replace(':clubSlug', clubSlug)
    .replace(':bookingId', bookingId)
    .replace(':eventId', eventId);
  return isHolder ? holderRoute : bookingRoute;
};

const getCustomerActions = ({
  action,
  row,
  holderCode,
  isHolder,
}: {
  action: BookingAction;
  row: EventReportBooking;
  holderCode?: string;
  isHolder?: boolean;
}) => {
  if (action.id === EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING)
    return () => action.method({ id: row.id, email: row.buyer.email });
  if (action.id === EVENT_REPORT_ACTION_TYPE_DOWNLOAD_PDF) return () => action.method({ id: row.token });
  if (action.id === EVENT_REPORT_ACTION_TYPE_SEE_DETAILS)
    return () =>
      action.method({
        id: row.id,
        holderCode,
        isHolder,
      });
  if (action.id === EVENT_REPORT_ACTION_TYPE_CANCEL) return () => action.method({ id: row.legacyId.toString() });
  return () => {
    return action.method({
      id: row.id,
      holderCode,
      ticketQuantity: row.quantity,
      legacyId: row.legacyId,
      ticketHolders: row.ticketHolders,
      price: row.offer.price.amount,
      onlinePrice: row.offer.price.onlinePrice,
      offerType: row.offer.type,
      buyerFirstName: row.buyer.firstName,
      buyerLastName: row.buyer.lastName,
      isNominative: row.offer.settings.isNominative,
    });
  };
};

const formatTicketHolder = ({
  ticketHolder,
  eventId,
  eventStatus,
  row,
  timezone,
  language,
  translations,
  actions,
  isActive,
  isSuperAdmin,
  manualCheckinsLeft,
}: {
  ticketHolder: TicketHolder;
  eventId: string;
  eventStatus: string;
  row: EventReportBooking;
  timezone: string;
  language: string;
  actions: BookingActions;
  translations: { [key: string]: string };
  isActive: boolean;
  isSuperAdmin: boolean;
  isSwapped: boolean;
  manualCheckinsLeft: number;
}) => {
  const addons = row.addOns.map((addon) => addon.name).join(',');
  const createdAt = fromBackend(row.createdAt).tz(timezone).format(DATE_FORMAT_SHORT_DATE_HOUR);
  const status = getStatus({
    refundQty: ticketHolder.isRefunded ? 1 : 0,
    price: row.price.onlinePrice,
    confirmed: row.confirmed,
    totalQty: ticketHolder.isRefunded ? 1 : 0,
    hasBeenSwapped: row.hasBeenSwapped && !ticketHolder.isActive,
    translations,
  });
  const attendees =
    ticketHolder.checkedIn && ticketHolder.checkedInAt
      ? fromBackend(ticketHolder.checkedInAt).tz(timezone).format(DATE_FORMAT_SHORT_TIME)
      : manualCheckinsLeft > 0
        ? translations.manualCheckin
        : translations.attendeesNoShow;
  const attendeesStatus = ticketHolder.checkedIn ? EVENT_REPORT_ATTENDEES_ALL : EVENT_REPORT_ATTENDEES_PARTIAL;
  const filteredActions = filterBookingActions({ actions, row, eventStatus, isSuperAdmin });

  return {
    id: row.id,
    eventId,
    isNominative: false,
    deppendsOn: row.legacyId,
    isActive,
    isClicked: isActive,
    code: ticketHolder.code,
    isSwapped: row.hasBeenSwapped && !ticketHolder.isActive,
    name: `${ticketHolder.firstName} ${ticketHolder.lastName}`,
    legacyId: ticketHolder.legacyId,
    offer: {
      name: row.offer.name,
      description: row.offer.description,
      type: row.offer.type,
      price: row.offer.price,
    },
    quantity: 1,
    price: currencyValueFormatter(row.offer.price.amount, language, row.offer.price.currency),
    createdAt,
    status,
    attendeesStatus,
    attendees,
    isRefunded: !row.confirmed || ticketHolder.isRefunded,
    addons,
    actions: filteredActions.map((action) => ({
      ...action,
      name:
        action.id === EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING && row.offer.type === EVENT_REPORT_PASS_TYPE
          ? translations.resendPass
          : action.name,
      action: getCustomerActions({ action, row, holderCode: ticketHolder.code, isHolder: true }),
    })),
  };
};

const formatBuyer = ({
  row,
  eventId,
  eventStatus,
  timezone,
  language,
  translations,
  actions,
  isClicked,
  isSwapped,
  isSuperAdmin,
}: {
  row: EventReportBooking;
  eventId: string;
  eventStatus: string;
  timezone: string;
  language: string;
  isClicked: boolean;
  actions: BookingActions;
  translations: { [key: string]: string };
  isSwapped: boolean;
  isSuperAdmin: boolean;
}) => {
  const addons = row.addOns.map((addon) => addon.name).join(',');
  const createdAt = fromBackend(row.createdAt).tz(timezone).format(DATE_FORMAT_SHORT_DATE_HOUR);
  const refundQty = row.ticketHolders.reduce((acc: number, holder) => {
    if (holder.isRefunded) return acc + 1;
    return acc;
  }, 0);
  const status = getStatus({
    refundQty,
    price: row.price.onlinePrice,
    confirmed: row.confirmed,
    totalQty: row.ticketHolders.length,
    hasBeenSwapped: row.hasBeenSwapped,
    translations,
  });
  const attendeesStatus =
    row.checkedInTickets < row.ticketHolders.length ? EVENT_REPORT_ATTENDEES_PARTIAL : EVENT_REPORT_ATTENDEES_ALL;
  const attendeesFormatted = row.checkedInTickets === 0 ? '0' : `${row.checkedInTickets}/${row.ticketHolders.length}`;
  const filteredActions = filterBookingActions({ actions, row, eventStatus, isSuperAdmin });

  return {
    id: row.id,
    eventId,
    isNominative: row.offer.settings.isNominative,
    deppendsOn: null,
    code: null,
    isActive: true,
    isClicked,
    isSwapped,
    name: `${row.buyer.firstName} ${row.buyer.lastName}`,
    legacyId: row.legacyId,
    offer: {
      name: row.offer.name,
      description: row.offer.description,
      type: row.offer.type,
      price: row.offer.price,
    },
    quantity: row.quantity,
    price: currencyValueFormatter(row.offer.price.amount, language, row.offer.price.currency),
    createdAt,
    status,
    attendeesStatus,
    attendees: attendeesFormatted,
    isRefunded: !row.confirmed || refundQty > 0,
    addons,
    actions: filteredActions.map((action) => ({
      ...action,
      name:
        action.id === EVENT_REPORT_ACTION_TYPE_RESEND_BOOKING && row.offer.type === EVENT_REPORT_PASS_TYPE
          ? translations.resendPass
          : action.name,
      action: getCustomerActions({ action, row, isHolder: false }),
    })),
  };
};

export const formatDataBookingTableSection = ({
  data,
  eventId,
  eventStatus,
  timezone,
  language,
  isTicketHolderActive,
  actions,
  isSuperAdmin,
  t,
}: {
  data: EventReportBooking[];
  eventStatus: string;
  eventId: string;
  timezone: string;
  language: string;
  isTicketHolderActive: boolean;
  actions: BookingActions;
  isSuperAdmin: boolean;
  t: TFunction<'translation', undefined>;
}) => {
  const translations = {
    activeLabel: t('eventReport:activeLabel'),
    refundedLabel: t('eventReport:refundedLabel'),
    resoldLabel: t('eventReport:resoldLabel'),
    attendeesNoShow: t('eventReport:noShowLabel'),
    resendPass: t('eventReport:resendPass'),
    cancelledLabel: t('eventReport:cancelledLabel'),
    manualCheckin: t('eventReport:manualCheckinLabel'),
  };
  const formattedData: EventBookingFormatted[] = [];

  data.forEach((row) => {
    let manualCheckins =
      row.checkedInTickets - row.ticketHolders.reduce((acc, holder) => (holder.checkedIn ? acc + 1 : acc), 1);

    const buyer = formatBuyer({
      row,
      eventId,
      eventStatus,
      timezone,
      language,
      translations,
      actions,
      isClicked: row.offer.settings.isNominative && isTicketHolderActive,
      isSwapped: row.hasBeenSwapped,
      isSuperAdmin,
    });
    const ticketHolders = row.ticketHolders
      .filter((_) => row.offer.settings.isNominative)
      .map((ticketHolder) => {
        if (!ticketHolder.checkedIn) manualCheckins = manualCheckins - 1;
        return formatTicketHolder({
          ticketHolder,
          eventId,
          eventStatus,
          row,
          language,
          timezone,
          translations,
          actions,
          isSuperAdmin,
          isActive: isTicketHolderActive,
          isSwapped: row.hasBeenSwapped && !ticketHolder.isActive,
          manualCheckinsLeft: manualCheckins,
        });
      });

    formattedData.push(buyer, ...ticketHolders);
  });

  return formattedData;
};

export const formatFiltersBookingTableSection = ({
  addons,
  admissions,
  language,
  isLiveOrPastEvent,
  t,
}: {
  addons: Addon[];
  admissions: AdmissionResponse;
  language: string;
  isLiveOrPastEvent: boolean;
  t: TFunction<'translation', undefined>;
}) => {
  return [
    {
      id: EVENT_REPORT_BOOKING_FILTER_REFUND_PARAM,
      title: t('eventReport:statusLabel'),
      content: [
        {
          id: EVENT_REPORT_BOOKING_FILTER_ACTIVE_TYPE,
          name: t('eventReport:activeLabel'),
          slug: EVENT_REPORT_BOOKING_FILTER_ACTIVE_TYPE,
          children: [],
        },
        {
          id: EVENT_REPORT_BOOKING_FILTER_REFUND_TYPE,
          name: t('eventReport:refundedResoldLabel'),
          slug: EVENT_REPORT_BOOKING_FILTER_REFUND_TYPE,
          children: [],
        },
      ],
    },
    ...(isLiveOrPastEvent
      ? [
          {
            id: EVENT_REPORT_BOOKING_FILTER_ATTENDANCE_PARAM,
            title: t('eventReport:attendanceTabLabel'),
            content: [
              {
                id: EVENT_REPORT_BOOKING_FILTER_ATTENDANCE_TYPE,
                name: t('eventReport:attendedLabel'),
                slug: EVENT_REPORT_BOOKING_FILTER_ATTENDANCE_TYPE,
                children: [],
              },
              {
                id: EVENT_REPORT_BOOKING_FILTER_NOT_ATTENDANCE_TYPE,
                name: t('eventReport:noShowLabel'),
                slug: EVENT_REPORT_BOOKING_FILTER_NOT_ATTENDANCE_TYPE,
                children: [],
              },
            ],
          },
        ]
      : []),
    {
      id: EVENT_REPORT_BOOKING_FILTER_OFFER_PARAM,
      title: t('offers:title'),
      content: BOOKING_FILTER_ORDER.map((type) => ({
        id: type,
        name: t(EVENT_REPORT_OLD_OFFER_TYPES_TRANSLATIONS[type]),
        slug: type,
        children: admissions[type as keyof typeof admissions].map((offer) => {
          const defaultname = getDefaultOfferNameTranslation(type) || getDefaultPassNameTranslation(type);
          const amount = currencyValueFormatter(offer.price.amount, language, offer.price.currency);
          const offerName: string =
            getTranslatedPropertyBasedOnOrder({
              translations: offer.name,
              preferredLanguage: language,
            }) ?? t(defaultname);

          return {
            id: offer.id,
            name: `${offerName}  - ${amount}`,
            slug: offer.id,
            children: [],
          };
        }),
      })),
    },
    {
      id: EVENT_REPORT_BOOKING_FILTER_ADDON_PARAM,
      title: t('eventReport:customerAddonsColumnTitle'),
      content: addons.map((addon) => {
        const defaultNameTranslated = getDefaultAddonNameTranslation(addon.type);
        const addonName =
          getTranslatedPropertyBasedOnOrder({
            translations: addon.name,
            preferredLanguage: language,
          }) ?? defaultNameTranslated;

        return {
          id: addon.id,
          name: addonName,
          slug: addon.id,
          children: [],
        };
      }),
    },
  ];
};
